import {
  ApolloError,
  gql,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import React from "react";

import {
  UpcomingClassesQuery,
  UpcomingClassesQueryVariables,
} from "../../shared/generated/graphql";

const upcomingClassesQuery = gql`
  query upcomingClasses(
    $uid: ID
    $includeSchedule: Boolean!
    $flexSectionsOffset: String!
    $meetingsOffset: String!
  ) {
    publicProfile: publicProfileNew(uid: $uid) {
      uid
      leader {
        uid
        name
        flexSections(first: 200, after: $flexSectionsOffset)
          @include(if: $includeSchedule) {
          edges {
            node {
              uid
              start_time
              end_time
              deleted_at
              canceled_at
              published_at
              isBuyable
              meetingCount

              leader {
                uid
                name
                teacherForSellerOrg {
                  uid
                }
              }

              activity {
                uid
                title
                titleTranslated
                age_min
                age_max
                is_ongoing_weekly
                weekly_meetings
                duration_weeks
                isClub
                details {
                  photo
                }
              }
            }
          }
        }
        meetings(first: 200, after: $meetingsOffset)
          @include(if: $includeSchedule) {
          edges {
            node {
              uid
              start_time
              end_time
              sectionSequenceNumber

              section {
                uid
                start_time
                end_time
                deleted_at
                canceled_at
                published_at
                isBuyable
                meetingCount
                nextOngoingMeeting {
                  uid
                  start_time
                }

                leader {
                  uid
                  name
                  teacherForSellerOrg {
                    uid
                    name
                  }
                }

                activity {
                  uid
                  title
                  titleTranslated
                  age_min
                  age_max
                  is_ongoing_weekly
                  weekly_meetings
                  duration_weeks
                  isClub
                  details {
                    photo
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export type UpcomingClassesQueryOptions = {
  variables: UpcomingClassesQueryVariables;
};

type UseUpcomingClassesQueryValue = {
  data: UpcomingClassesQuery;
  loading: boolean;
  error: ApolloError | undefined;
  fetchMoreClasses: () => Promise<void>;
  isFetchingMore: boolean;
  canFetchMore: boolean;
};

export const useUpcomingClassesQuery = (
  options: UpcomingClassesQueryOptions,
): UseUpcomingClassesQueryValue => {
  const { data, loading, error, fetchMore } = useQueryWithPreviousData<
    UpcomingClassesQuery,
    UpcomingClassesQueryVariables
  >(upcomingClassesQuery, {
    ...options,
    variables: options.variables,
  });

  const [canFetchMore, setCanFetchMore] = React.useState(true);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);

  const fetchMoreClasses = React.useCallback(async () => {
    if (isFetchingMore || !canFetchMore) {
      return;
    }

    setIsFetchingMore(true);

    // @ts-ignore TS(2339): Property 'meetings' does not exist on type '{ __ty... Remove this comment to see the full error message
    const { meetings: prevMeetings, flexSections: prevFlexSections } =
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      data.publicProfile.leader;

    try {
      await fetchMore({
        variables: {
          ...options.variables,
          flexSectionsOffset: prevFlexSections.edges.length.toString(),
          meetingsOffset: prevMeetings.edges.length.toString(),
        },
        updateQuery: (
          previousResult: UpcomingClassesQuery,
          { fetchMoreResult },
        ) => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          // @ts-ignore TS(2339): Property 'meetings' does not exist on type '{ __ty... Remove this comment to see the full error message
          const { meetings, flexSections } =
            // @ts-ignore TS(2531): Object is possibly 'null'.
            fetchMoreResult.publicProfile.leader;

          const moreClasses =
            flexSections.edges.length > 0 || meetings.edges.length > 0;

          if (!moreClasses) {
            setCanFetchMore(false);
            return previousResult;
          }

          return Object.assign({}, previousResult, {
            publicProfile: {
              __typename: "PublicProfile",
              ...previousResult.publicProfile,
              ...fetchMoreResult.publicProfile,
              leader: {
                // @ts-ignore TS(2531): Object is possibly 'null'.
                ...previousResult.publicProfile.leader,
                // @ts-ignore TS(2531): Object is possibly 'null'.
                ...fetchMoreResult.publicProfile.leader,
                flexSections: {
                  // @ts-ignore TS(2531): Object is possibly 'null'.
                  ...previousResult.publicProfile.leader.flexSections,
                  // @ts-ignore TS(2531): Object is possibly 'null'.
                  ...fetchMoreResult.publicProfile.leader.flexSections,
                  edges:
                    // @ts-ignore TS(2531): Object is possibly 'null'.
                    previousResult.publicProfile.leader.flexSections.edges.concat(
                      // @ts-ignore TS(2531): Object is possibly 'null'.
                      fetchMoreResult.publicProfile.leader.flexSections.edges,
                    ),
                },
                meetings: {
                  // @ts-ignore TS(2531): Object is possibly 'null'.
                  ...previousResult.publicProfile.leader.meetings,
                  // @ts-ignore TS(2531): Object is possibly 'null'.
                  ...fetchMoreResult.publicProfile.leader.meetings,
                  edges:
                    // @ts-ignore TS(2531): Object is possibly 'null'.
                    previousResult.publicProfile.leader.meetings.edges.concat(
                      // @ts-ignore TS(2531): Object is possibly 'null'.
                      fetchMoreResult.publicProfile.leader.meetings.edges,
                    ),
                },
              },
            },
          });
        },
      });
    } finally {
      setIsFetchingMore(false);
    }
  }, [canFetchMore, data, fetchMore, isFetchingMore, options]);

  return {
    // @ts-ignore TS(2322): Type 'UpcomingClassesQuery | undefined' is not ass... Remove this comment to see the full error message
    data,
    loading,
    isFetchingMore,
    canFetchMore,
    error,
    fetchMoreClasses,
  };
};
